import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '../../hooks/useAuth';
import { GymStaffManager, type InheritedRoles } from '../../components/gym/GymStaffManager';
import { staffService, type Staff } from '../../services/staff';
import { useCurrentLocation, type CurrentLocation, type HostingPlace } from '../hooks/useCurrentLocation';

/**
 * Who works here. Shared by every kind of place: the staff endpoints hang off
 * `Location`, so all this page needs is the id of whichever one is selected.
 * The kind goes down too, because the roles worth granting differ — a circuit
 * has nobody setting boulders, and a stage is where a judge is a real job.
 *
 * A competition is also run by whoever stands behind it — its circuit's staff,
 * the hosting gym's — with the roles they hold there and no row here. The
 * manager below lists only the people named on the competition itself, so the
 * page first shows who else is in charge, read from those places and edited
 * there. The manager is told what each of them already holds: the server reads
 * the union, so a row here can only add to it, and the picker says so.
 */
export default function StaffPage() {
  const { user } = useAuth();
  const { location, loading } = useCurrentLocation();
  const groups = useInheritedStaff(location);
  const inheritedRoles = useMemo(() => inheritedRolesOf(groups), [groups]);

  if (loading) return <Skeleton className="h-96 w-full max-w-4xl" />;
  if (!location) return null;

  return (
    <div className="max-w-4xl space-y-6">
      <InheritedStaff location={location} groups={groups} />
      <GymStaffManager
        gymId={location.id}
        currentUserSlug={user?.slug}
        locationType={location.type}
        inheritedRoles={inheritedRoles}
      />
    </div>
  );
}

/** The confirmed staff of one place a competition hangs off, as they were loaded. */
interface InheritedFrom {
  place: HostingPlace;
  staff: Staff[];
}

/** The places a competition hangs off, in the order they are named. */
function hostsOf(trophy: HostingPlace | null, venue: HostingPlace | null): HostingPlace[] {
  return [trophy, venue].filter((place): place is HostingPlace => !!place);
}

/**
 * The confirmed staff of each place `location` hangs off, read straight off
 * each place's own staff list: that is the truth the server grants from, and
 * a copy here would be a second one to keep in step. Null while loading; empty
 * for a place that hangs off nothing.
 */
function useInheritedStaff(location: CurrentLocation | null): InheritedFrom[] | null {
  const trophy = location?.trophy ?? null;
  const venue = location?.venue ?? null;
  const [groups, setGroups] = useState<InheritedFrom[] | null>(null);

  useEffect(() => {
    const places = hostsOf(trophy, venue);
    if (places.length === 0) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    setGroups(null);
    Promise.all(
      places.map(async (place) => ({
        place,
        staff: (await staffService.getLocationStaff(place.slug).catch(() => [])).filter(
          (staff) => staff.status === 'confirmed'
        ),
      }))
    ).then((loaded) => {
      if (!cancelled) setGroups(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [trophy, venue]);

  return groups;
}

/**
 * Who holds which role through which place, keyed on the user's slug. The
 * first place to grant a role names it: a stage is the circuit's, whoever
 * hosts it, and the circuit is named first.
 */
function inheritedRolesOf(groups: InheritedFrom[] | null): InheritedRoles {
  const byUser: InheritedRoles = {};
  for (const { place, staff } of groups ?? []) {
    for (const member of staff) {
      const roles = (byUser[member.user.slug] ??= {});
      for (const role of member.staff_roles) roles[role] ??= place.name;
    }
  }
  return byUser;
}

function InheritedStaff({ location, groups }: { location: CurrentLocation; groups: InheritedFrom[] | null }) {
  const { t } = useTranslation();
  const from = hostsOf(location.trophy ?? null, location.venue ?? null);

  if (from.length === 0) return null;

  return (
    <Card data-testid="inherited-staff">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="size-4" aria-hidden />
          {t('gymAdmin.staffPage.inheritedTitle')}
        </CardTitle>
        <CardDescription>
          {t('gymAdmin.staffPage.inheritedDescription', { places: from.map((place) => place.name).join(', ') })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {groups === null && <Skeleton className="h-20 w-full" />}
        {groups?.map(({ place, staff }) => (
          <div key={place.slug} className="space-y-2" data-testid="inherited-staff-group">
            <h3 className="text-sm font-medium">{place.name}</h3>
            {staff.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('gymAdmin.staffPage.inheritedNone')}</p>
            ) : (
              staff.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between gap-3 rounded-lg border p-3"
                  data-testid="inherited-staff-row"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{member.user.full_name}</div>
                    <div className="text-sm text-muted-foreground">@{member.user.username}</div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {member.staff_roles.map((role) => (
                      <Badge key={role} variant="secondary">
                        {t(`gymSettings.${role}Role`)}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
